/**
 * Pure business logic for determining which step to show on mount.
 * Extracted from UnifiedBankingSignupFlow useEffect for testability.
 *
 * Pattern mirrors computeShouldShowOverlay / determineStepProgression —
 * pure function in, deterministic result out, no React.
 */

import type { SubstepDefinition } from './stepDefinitions';
import {
  StepMode,
  findSubstepByActivationStep,
  findSubstepByOnboardingStep,
  ONBOARDING_STEPS_ORDER,
} from './stepDefinitions';
import type { SwanActivationStepIdType } from '../ActivationFlow/constants/activationStepIds';
import type { SwanOnboardingStepIdType } from 'components/pages/Banks/BankingSignUp/Onboarding/constants/onboardingStepIds';
import {
  shouldAutoSkipIssueCard,
  shouldAutoSkipCardDesign,
  applyAutoSkip,
} from './autoSkipLogic';

// ─── Types ────────────────────────────────────────────────────────────

interface Card {
  status?: string;
  [key: string]: any;
}

export interface StepInitializationInput {
  currentMode: StepMode;

  /** Cards list (for auto-skip logic, post-account only) */
  cards: Card[] | undefined;
  isLoadingCards: boolean;

  /** Activation status (post-account only) */
  activationFurthestStepReached: SwanActivationStepIdType | null | undefined;
  isLoadingActivationStatus: boolean;

  /** Onboarding status (pre-account only) */
  furthestStepCompleted?: SwanOnboardingStepIdType | null;
  isLoadingOnboardingStatus?: boolean;

  /** Feature flag for post-account flow */
  isActivationFlowEnabled: boolean;

  /** All visible substeps (filtered by mode and country) */
  visibleSubsteps: SubstepDefinition[];

  /** Props from the component */
  initialStepId?: string;
  initialScreenIndex?: number;
}

export interface StepInitializationResult {
  type: 'READY' | 'NOT_READY';
  stepIndex: number;
  autoSkippedSteps: Set<string>;
}

const NOT_READY: StepInitializationResult = {
  type: 'NOT_READY',
  stepIndex: 0,
  autoSkippedSteps: new Set(),
};

function ready(
  stepIndex: number,
  autoSkippedSteps?: Set<string>,
): StepInitializationResult {
  return {
    type: 'READY',
    stepIndex,
    autoSkippedSteps: autoSkippedSteps ?? new Set(),
  };
}

// ─── Main function ────────────────────────────────────────────────────

/**
 * Determines the initial step index when the signup flow mounts.
 *
 * Returns NOT_READY when data is still loading (useEffect should bail out).
 * Returns READY with the computed stepIndex once all required data is available.
 *
 * Three resolution strategies in priority order:
 *   1. Resume from explicit step ID (e.g. after Swan redirect)
 *   2. Post-account: resume from backend activation status
 *   3. Pre-account: start from beginning
 */
export function computeInitialStep(
  input: StepInitializationInput,
): StepInitializationResult {
  const {
    currentMode,
    cards,
    isLoadingCards,
    activationFurthestStepReached,
    isLoadingActivationStatus,
    isActivationFlowEnabled,
    visibleSubsteps,
    initialStepId,
    initialScreenIndex,
  } = input;

  // Guard: post-account needs cards data before we can decide (auto-skip)
  if (currentMode === StepMode.PostAccount && isLoadingCards) {
    return NOT_READY;
  }

  // ── Priority 1: Resume from explicit step ID ────────────────────────
  if (initialStepId) {
    const result = resolveFromStepId(initialStepId, cards, visibleSubsteps);
    if (result) return result;
    // Fall through if step not found
  }

  // ── Priority 2: Post-account initialization ─────────────────────────
  if (currentMode === StepMode.PostAccount && isActivationFlowEnabled) {
    if (isLoadingActivationStatus) return NOT_READY;

    return resolvePostAccount(
      activationFurthestStepReached ?? null,
      cards,
      visibleSubsteps,
    );
  }

  // ── Priority 3: Pre-account initialization ──────────────────────────
  if (currentMode === StepMode.PreAccount) {
    if (input.isLoadingOnboardingStatus) return NOT_READY;

    return resolvePreAccount(
      input.furthestStepCompleted ?? null,
      visibleSubsteps,
    );
  }

  return ready(initialScreenIndex || 0);
}

// ─── Strategy: Resume from step ID ────────────────────────────────────

function resolveFromStepId(
  stepId: string,
  cards: Card[] | undefined,
  visibleSubsteps: SubstepDefinition[],
): StepInitializationResult | null {
  const stepIndex = visibleSubsteps.findIndex((s) => s.id === stepId);
  if (stepIndex === -1) return null;

  const cardDesignSkipResult = shouldAutoSkipCardDesign(cards, stepId);
  if (cardDesignSkipResult.shouldSkip) {
    const targetIndex = applyAutoSkip(
      stepId,
      cardDesignSkipResult,
      visibleSubsteps,
    );
    if (targetIndex !== -1) {
      return ready(targetIndex, new Set([cardDesignSkipResult.targetStepId!]));
    }
  }

  return ready(stepIndex);
}

// ─── Strategy: Post-account (activation backend) ──────────────────────

function resolvePostAccount(
  furthestStep: SwanActivationStepIdType | null,
  cards: Card[] | undefined,
  visibleSubsteps: SubstepDefinition[],
): StepInitializationResult {
  if (furthestStep !== null && furthestStep !== undefined) {
    return resolveFromActivationProgress(furthestStep, cards, visibleSubsteps);
  }
  return resolveFirstTimeActivation(cards, visibleSubsteps);
}

/** User has existing activation progress — resume from backend step */
function resolveFromActivationProgress(
  backendStep: SwanActivationStepIdType,
  cards: Card[] | undefined,
  visibleSubsteps: SubstepDefinition[],
): StepInitializationResult {
  const substep = findSubstepByActivationStep(backendStep);
  if (!substep) return resolveFirstTimeActivation(cards, visibleSubsteps);

  const stepIndex = visibleSubsteps.findIndex((s) => s.id === substep.id);
  if (stepIndex === -1) {
    const fallback = visibleSubsteps.findIndex(
      (s) => s.mode === StepMode.PostAccount,
    );
    return ready(Math.max(0, fallback));
  }

  const skipResult = shouldAutoSkipIssueCard(cards, substep.id);
  if (skipResult.shouldSkip) {
    const targetIndex = applyAutoSkip(substep.id, skipResult, visibleSubsteps);
    if (targetIndex !== -1) {
      return ready(targetIndex, new Set([skipResult.targetStepId!]));
    }
  }

  return ready(stepIndex);
}

/** First-time activation — find the first post-account step with backend tracking */
function resolveFirstTimeActivation(
  cards: Card[] | undefined,
  visibleSubsteps: SubstepDefinition[],
): StepInitializationResult {
  const firstBackendStepIndex = visibleSubsteps.findIndex(
    (s) => s.mode === StepMode.PostAccount && s.activationStep,
  );

  if (firstBackendStepIndex !== -1) {
    const startIndex = firstBackendStepIndex;
    const firstStep = visibleSubsteps[firstBackendStepIndex];

    const skipResult = shouldAutoSkipIssueCard(cards, firstStep?.id);
    if (skipResult.shouldSkip) {
      const targetIndex = applyAutoSkip(
        firstStep.id,
        skipResult,
        visibleSubsteps,
      );
      if (targetIndex !== -1) {
        return ready(targetIndex, new Set([skipResult.targetStepId!]));
      }
    }

    return ready(startIndex);
  }

  // Fallback: any post-account step
  const fallback = visibleSubsteps.findIndex(
    (s) => s.mode === StepMode.PostAccount,
  );
  if (fallback !== -1) return ready(fallback);

  return ready(0);
}

// ─── Strategy: Pre-account (onboarding backend) ───────────────────────
// Mirrors resolvePostAccount / resolveFromActivationProgress pattern.

function resolvePreAccount(
  furthestStep: SwanOnboardingStepIdType | null,
  visibleSubsteps: SubstepDefinition[],
): StepInitializationResult {
  if (furthestStep !== null && furthestStep !== undefined) {
    return resolveFromOnboardingProgress(furthestStep, visibleSubsteps);
  }
  return resolveFirstTimeOnboarding(visibleSubsteps);
}

/** User has existing onboarding progress — resume from backend step */
function resolveFromOnboardingProgress(
  backendStep: SwanOnboardingStepIdType,
  visibleSubsteps: SubstepDefinition[],
): StepInitializationResult {
  const substep = findSubstepByOnboardingStep(backendStep);
  if (!substep) return resolveFirstTimeOnboarding(visibleSubsteps);

  const stepIndex = visibleSubsteps.findIndex((s) => s.id === substep.id);
  if (stepIndex !== -1) return ready(stepIndex);

  // Mapped substep not visible (e.g. country-specific step filtered out).
  // Walk forward through the canonical order to find the next visible step.
  const currentOrderIdx = ONBOARDING_STEPS_ORDER.indexOf(backendStep);
  for (let i = currentOrderIdx + 1; i < ONBOARDING_STEPS_ORDER.length; i++) {
    const nextSubstep = findSubstepByOnboardingStep(ONBOARDING_STEPS_ORDER[i]);
    if (nextSubstep) {
      const nextIndex = visibleSubsteps.findIndex(
        (s) => s.id === nextSubstep.id,
      );
      if (nextIndex !== -1) return ready(nextIndex);
    }
  }

  const fallback = visibleSubsteps.findIndex(
    (s) => s.mode === StepMode.PreAccount,
  );
  return ready(Math.max(0, fallback));
}

/** First-time onboarding — find the first pre-account step with backend tracking */
function resolveFirstTimeOnboarding(
  visibleSubsteps: SubstepDefinition[],
): StepInitializationResult {
  const firstOnboardingStepIndex = visibleSubsteps.findIndex(
    (s) => s.mode === StepMode.PreAccount && s.onboardingStep,
  );

  if (firstOnboardingStepIndex !== -1) {
    return ready(firstOnboardingStepIndex);
  }

  const fallback = visibleSubsteps.findIndex(
    (s) => s.mode === StepMode.PreAccount,
  );
  if (fallback !== -1) return ready(fallback);

  return ready(0);
}
